import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { Callback0Arg } from "abweb/utils/callback/Callback0Arg";
import { Callback0ArgPredicate } from "abweb/utils/callback/Callback0ArgPredicate";
import { EventListenerManager } from "abweb/utils/EventRegistration";
import { LogUtils } from "abweb/utils/log/LogUtils";

// The linter doesn't know that the eventListeners have been bound.  Turn off the rule for this file.
/*eslint-disable @typescript-eslint/unbound-method */

export class TeardownService {
  constructor() {
    // private readonly preventAppUnloadVoters: VoterListenerManager = new VoterListenerManager();
    _defineProperty(this, "preventAppUnloadVoters", new Callback0ArgPredicate());
    _defineProperty(this, "teardownListeners", new Callback0Arg());
    _defineProperty(this, "eventListeners", new EventListenerManager());
    _defineProperty(this, "teardownInProgress", false);
    this.eventListeners.registerEventListener(window, "unload", this.unloadHandler.bind(this));
    this.eventListeners.registerEventListener(window, "beforeunload", this.beforeUnloadHandler.bind(this));
  }
  destroy() {
    this.eventListeners.unregister();
  }

  /**
   * The preventAppUnloadFn will be invoked when user is trying to navigate away from the loaded application.
   * Return 'true' to cause the user to be prompted as to whether they really want to leave (this is currently the
   * best we can do).  Note that the teardown listeners will be invoked when the app (the current browser tab) is unloaded.
   * @param {() => boolean} preventAppUnloadVoter
   */
  get preventAppUnloadVotersRegistration() {
    return this.preventAppUnloadVoters;
  }
  get teardownListenersRegistration() {
    return this.teardownListeners;
  }

  // For now, ONLY invoked by unloadHandler when browser tab is being unloaded
  teardown() {
    if (this.teardownInProgress) {
      TeardownService.LOG.warn("Teardown.teardown() already invoked.  Ignoring subsequent invocations.");
      return;
    }
    this.coreTeardown();

    /*
    * EGE-4495: modified code so that callAllInReverseOrder() is called when using firefox.
    * Older versions of firefox would reject any pending promise while the browser window was unloading,
    * leading to orphaned error dialogs during a page refresh. This should not be an issue with
    * firefox versions 115+, so checking the browser type should no longer be necessary.
    */

    // Call in reverse order as later registered services may require earlier services during their teardown
    this.teardownListeners.callAllInReverseOrder();
  }
  get isTeardownInProgress() {
    return this.teardownInProgress;
  }
  coreTeardown() {
    // See EI-16310 why need to also remove event listeners (prevent (before)Unload handlers from being invoked)
    this.teardownInProgress = true;
    this.eventListeners.unregister();
  }

  /**
   * Function attached to window.onbeforeunload, called whenever the user navigates away from the app
   * or closes the tab/page. See CAT-905, but best we can do is to get browser to display generic dialog
   * when navigating away from app when app has unsaved changes.
   */
  beforeUnloadHandler(e) {
    if (this.preventAppUnloadVoters.callUntilFirstTrue()) {
      // Note that the actual text is ignored, but the fact that you're returning text causes browser to
      // display a generic browser-specific dialog.  Could only get it work reliably when using the
      // window.addEventListener("beforeunload", fn) registration mechanism (as opposed to window.beforeunload = fn)
      // if I set both e.returnValue AND return a string (despite some MDN examples that claim you only have to
      // return a string)

      // Note that if they wish to still leave the site, the below unload handler will be invoked
      e.returnValue = "You have unsaved changes. Do you wish to leave without saving?";
      return e.returnValue;
    } else {
      // This teardown has a fighting chance of being executed, but should really
      // be handled by unloadHandler via navigator.sendBeacon...
      // Note that there is a potential issue if any spawned async tasks do NOT complete before the unloadHandler
      // starts running, as once the unloadHander starrts the micro-task queues (which handles async tasks) are shutdown.
      // See below unloadHandler comments for more detail.
      this.teardown();

      // Continue directly to unload (invoking unload handler below)
      return null;
    }
  }
  unloadHandler() {
    // CAT-935
    // Note async invocation (including rpcs) is basically disabled in browsers during unload.
    // Instead, suggestion is to use Navigator.sendBeacon functionality
    // See http://usefulangle.com/post/62/javascript-send-data-to-server-on-page-exit-reload-redirect
    // The navigator.sendBeacon spec is also good reading as it docs the various creative hacks
    // that devs use to get around lack of async support during unload
    this.teardown();
  }

  /**
   * Prevent listeners (which can contain rpcs) from being invoked when unloading current url (current browser tab).
   * Once disabled, can not be re-enabled.  Typically only explicit logout workflows will invokes this.
   */
  disable() {
    this.coreTeardown();
  }
}
_defineProperty(TeardownService, "LOG", LogUtils.getLogger(__filename));
//# sourceMappingURL=TeardownService.js.map
